import { type FC, useId } from "react";
import "./PlatformSelector.css";
import cn from "classnames";
import type { Platform, PlatformValue } from "shared";
import { PlatformIcon } from "../common/PlatformIcon";
import { SimpleTooltip } from "../common/SimpleTooltip";
import { Button } from "./Button";

type Props = {
	platforms: Platform[] | PlatformValue[];
	values: Set<Platform | PlatformValue>;
	onChange: (value: Platform | PlatformValue) => void;
	/**
	 * When provided, platforms outside this set are shown as not connected:
	 * de-emphasized and labelled, but still selectable.
	 */
	connected?: Set<Platform | PlatformValue>;
};

const tooltipMap: Record<Platform, string> = {
	facebook: "Facebook toggle",
	instagram: "Instagram toggle",
	threads: "Threads toggle",
	linkedin: "Linkedin toggle",
	wordpress: "WordPress toggle",
	"custom-blog": "Custom blog",
	"google-business": "Google Business toggle",
	youtube: "YouTube toggle",
	tiktok: "TikTok toggle",
	x: "X toggle",
};

export const PlatformSelector: FC<Readonly<Props>> = ({
	platforms,
	values,
	onChange,
	connected,
}) => {
	const key = useId();
	return (
		<div className="platform-selector">
			{platforms.map((platform) => {
				const isUnconnected =
					connected !== undefined && !connected.has(platform);
				const label = isUnconnected
					? `${tooltipMap[platform]} — not connected, content will be drafted but can't publish automatically`
					: tooltipMap[platform];
				return (
					<SimpleTooltip
						key={`${key}-${platform}`}
						tooltip={label}
						placement="bottom"
					>
						<Button
							aria-label={label}
							className={cn({
								"border-primary": values.has(platform),
								"platform-unconnected": isUnconnected,
							})}
							variant="social"
							aria-pressed={values.has(platform)}
							onClick={() => onChange(platform)}
						>
							<PlatformIcon value={platform} />
						</Button>
					</SimpleTooltip>
				);
			})}
		</div>
	);
};
